import { useEffect, useMemo, useState } from "react"
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  // isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns"

import {
  // DroppableCell,
  EventGap,
  EventHeight,
  // EventItem,
  getAllEventsForDay,
  getEventsForDay,
  getSpanningEventsForDay,
  sortEvents,
  useEventVisibility,
  type CalendarEvent,
} from "../components"
// import { Popover, PopoverContent, PopoverTrigger } from "@workspace/web-ui/components/popover"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@workspace/web-ui/components/sheet"
import { ExpandableAppointmentCard } from "./expandable-appointments"
// import { useEventModalStore } from "../store"
// import { AppointmentPopover } from "./appointment-popover"
// import React from "react"
// import { RefillContent } from "./refill-content"

interface MonthViewProps {
  currentDate: Date
  events: CalendarEvent[]
  onEventSelect: (event: CalendarEvent) => void
  onEventCreate: (startTime: Date) => void
}

export function MonthView({ currentDate, events }: MonthViewProps) {
  const [selectedDay, setSelectedDay] = useState<Date | null>(null)
  const [expandedAppointmentId, setExpandedAppointmentId] = useState<string | null>(null)
  const days = useMemo(() => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(monthStart)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }, [currentDate])

  const weekdays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const date = addDays(startOfWeek(new Date()), i)
      return format(date, "EEE")
    })
  }, [])

  const weeks = useMemo(() => {
    const result = []
    let week = []

    for (let i = 0; i < days.length; i++) {
      week.push(days[i])
      if (week.length === 7 || i === days.length - 1) {
        result.push(week)
        week = []
      }
    }

    return result
  }, [days])

  const handleDayClick = (day: Date) => {
    setSelectedDay(day)
  }

  const [isMounted, setIsMounted] = useState(false)
  const { contentRef, getVisibleEventCount } = useEventVisibility({
    eventHeight: EventHeight,
    eventGap: EventGap,
  })

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <>
      <div className="border-border/70 grid grid-cols-7 border-b">
        {weekdays.map((day) => (
          <div key={day} className="text-muted-foreground/70 py-2 text-center text-sm">
            {day}
          </div>
        ))}
      </div>
      <div className="grid flex-1 auto-rows-fr">
        {weeks.map((week, weekIndex) => (
          <div key={`week-${weekIndex}`} className="grid grid-cols-7 [&:last-child>*]:border-b-0">
            {week.map((day, dayIndex) => {
              if (!day) return null

              const dayEvents = getEventsForDay(events, day)
              const spanningEvents = getSpanningEventsForDay(events, day)
              const isCurrentMonth = isSameMonth(day, currentDate)
              const allDayEvents = [...spanningEvents, ...dayEvents]
              // const allEvents = getAllEventsForDay(events, day)

              const isReferenceCell = weekIndex === 0 && dayIndex === 0
              const visibleCount = isMounted ? getVisibleEventCount(allDayEvents.length) : undefined
              const hasMore = visibleCount !== undefined && allDayEvents.length > visibleCount

              return (
                <div
                  key={day.toString()}
                  className="group border-border/70 data-outside-cell:bg-muted/25 data-outside-cell:text-muted-foreground/70 border-r border-b last:border-r-0 cursor-pointer hover:bg-[#F5F5F5]"
                  data-today={isToday(day) || undefined}
                  data-outside-cell={!isCurrentMonth || undefined}
                  onClick={() => handleDayClick(day)}
                >
                  <div className="p-2">
                    <div className={`mt-1 inline-flex size-6 w-full items-center justify-center rounded-full text-sm `}>
                      <div className={`size-8 rounded-full flex items-center justify-center ${
                      isToday(day) ? 'bg-blue-500 text-white' : ''
                    }`}>
                        {format(day, "d")}
                      </div>
                    </div>
                    <div
                      ref={isReferenceCell ? contentRef : null}
                      className="min-h-[calc((var(--event-height)+var(--event-gap))*1)] sm:min-h-[calc((var(--event-height)+var(--event-gap))*2)] lg:min-h-[calc((var(--event-height)+var(--event-gap))*4)] flex gap-1"
                    >
                      {sortEvents(allDayEvents).map((event, index) => {
                        const isHidden = isMounted && visibleCount && index >= visibleCount

                        if (!visibleCount || isHidden) return null

                        return (
                          <div
                            key={event.id}
                            className="aria-hidden:hidden flex mt-2"
                            aria-hidden={isHidden ? "true" : undefined}
                          >
                            {event.type === 'refill' ? (
                              <div className={`size-3 rounded-full ${
                                event.status === 'pending' ? 'bg-orange-400' :
                                event.status === 'accepted' ? 'border bg-blue-600' :
                                event.status === 'rejected' ? 'bg-red-400' : 'bg-blue-600'
                              }`} />
                            ) : (
                              <div className={`size-3 rounded-full ${
                                event.status === 'scheduled' ? 'border border-green-600' :
                                event.status === 'completed' ? 'bg-green-500' :
                                event.status === 'cancelled' ? 'bg-gray-300' : 'bg-green-600'
                              }`} />
                            )}
                          </div>
                        )
                      })}

                      {hasMore && (
                        <div className="mt-2 text-xs text-gray-500">
                          +{allDayEvents.length - (visibleCount || 0)}
                        </div>
                      )}
                    </div>
                    <p className="hidden sm:block text-xs text-center text-gray-500">
                      {allDayEvents.length > 0 
                      ? `${allDayEvents.length} ${allDayEvents.some(event => event.type === 'refill') ? 'refills' : 'appointments'}` 
                      : ``}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>

      <Sheet open={selectedDay !== null} onOpenChange={(open) => !open && setSelectedDay(null)}>
        <SheetContent title="calendar-month-view" className="h-screen sm:max-w-[600px] w-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          <SheetHeader>
            <SheetTitle>
              {selectedDay && format(selectedDay, "EEEE, MMMM d")}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-2">
            {selectedDay && getAllEventsForDay(events, selectedDay).map((event) => (
              <ExpandableAppointmentCard 
              key={event.id}
              appointment={event} 
              isExpanded={event.id === expandedAppointmentId}
              onToggle={(isExpanding) => {
                setExpandedAppointmentId(isExpanding ? event.id : null)
              }}
            />
            ))}
            {selectedDay && getAllEventsForDay(events, selectedDay).length === 0 && (
              <div className="text-center text-gray-500 py-4">
                No events scheduled for this day
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

